import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Search, Plus, Pencil, Trash2, ImageIcon, X, Upload, FolderTree, BookOpen, Lock, Eye } from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Pagination } from '@/components/shared/pagination'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/misc'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { useMyPermissions } from '@/hooks/use-settings'
import {
  formatProductPrice,
  resolveImageUrl,
  PRICE_TYPES,
  PRICE_TYPE_LABELS,
  PRODUCT_STATUSES,
  PRODUCT_STATUS_LABELS,
  productStatusVariant,
  isSyncedProduct,
  PRODUCT_SOURCE_LABELS,
  useProducts,
  useProductCategories,
  useCreateProduct,
  useUpdateProduct,
  useDeleteProduct,
  useUploadProductImage,
  useCreateProductCategory,
  useUpdateProductCategory,
  useDeleteProductCategory,
  type Product,
  type ProductInput,
  type ProductCategory,
  type ProductCategoryInput,
} from '@/hooks/use-products'
import { Link } from 'react-router-dom'

const LIMIT = 20
const ALL = '__all__'
const NONE = '__none__'

/**
 * Quyền thao tác sản phẩm / danh mục — khớp backend product-routes.ts (`products.create` / `update` / `delete`, theo
 * vai trò động ở Cài đặt → Vai trò & quyền). Thiếu quyền nào thì ẩn nút đó (BRD §5.3.1); quyền chưa tải coi như không có.
 */
function useProductPerms() {
  const { data: perms } = useMyPermissions()
  return {
    create: !!perms?.has('products.create'),
    update: !!perms?.has('products.update'),
    delete: !!perms?.has('products.delete'),
  }
}

// ── Field helper ─────────────────────────────────────────────────────
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  )
}

/** Sắp xếp danh mục cây theo cha-con, kèm độ sâu để thụt lề. */
function flattenTree<T extends { id: string; parentId: string | null; sortOrder?: number; name: string }>(
  items: T[],
): Array<T & { depth: number }> {
  const byParent = new Map<string | null, T[]>()
  for (const it of items) {
    const key = it.parentId ?? null
    const arr = byParent.get(key) ?? []
    arr.push(it)
    byParent.set(key, arr)
  }
  const ids = new Set(items.map((i) => i.id))
  const out: Array<T & { depth: number }> = []
  const walk = (parentId: string | null, depth: number) => {
    const children = (byParent.get(parentId) ?? []).sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name),
    )
    for (const c of children) {
      out.push({ ...c, depth })
      walk(c.id, depth + 1)
    }
  }
  // Gốc = không có cha, hoặc cha không tồn tại (mồ côi) → coi là gốc.
  walk(null, 0)
  for (const it of items) {
    if (it.parentId && !ids.has(it.parentId) && !out.find((o) => o.id === it.id)) {
      out.push({ ...it, depth: 0 })
    }
  }
  return out
}

// ── Trang chính ──────────────────────────────────────────────────────
export function ProductsPage() {
  const { t } = useTranslation('aiComp')
  return (
    <div className="space-y-6">
      <PageHeader
        title={t('productsPage.title')}
        description={t('productsPage.description')}
      />
      <p className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-sm">
        <BookOpen className="h-4 w-4 text-primary" />
        {t('productsPage.kbMoved')}
        <Link to="/ai/knowledge" className="font-medium text-primary hover:underline">{t('productsPage.openKb')}</Link>
      </p>
      <Tabs defaultValue="products">
        <TabsList>
          <TabsTrigger value="products">{t('productsPage.tabProducts')}</TabsTrigger>
          <TabsTrigger value="product-categories">{t('productsPage.tabCategories')}</TabsTrigger>
        </TabsList>
        <TabsContent value="products" className="pt-2">
          <ProductsTab />
        </TabsContent>
        <TabsContent value="product-categories" className="pt-2">
          <ProductCategoriesTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════
// TAB 1 — Sản phẩm
// ════════════════════════════════════════════════════════════════════
function ProductsTab() {
  const { t } = useTranslation('aiComp')
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [categoryId, setCategoryId] = useState(ALL)
  const [status, setStatus] = useState(ALL)
  const [source, setSource] = useState(ALL)
  const [editing, setEditing] = useState<Product | null>(null)
  const [creating, setCreating] = useState(false)
  const [viewing, setViewing] = useState<Product | null>(null)
  const quyen = useProductPerms()

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  const params = useMemo(
    () => ({
      page,
      pageSize: LIMIT,
      search: search || undefined,
      categoryId: categoryId === ALL ? undefined : categoryId,
      status: status === ALL ? undefined : status,
      source: source === ALL ? undefined : source,
    }),
    [page, search, categoryId, status, source],
  )

  const { data, isLoading, isError } = useProducts(params)
  const { data: categories } = useProductCategories()
  const del = useDeleteProduct()

  const onDelete = (p: Product) => {
    if (!confirm(t('productsPage.confirmDelete', { name: p.name }))) return
    del.mutate(p.id, {
      onSuccess: () => toast.success(t('productsPage.deleted')),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  const columns: Column<Product>[] = [
    {
      key: 'image',
      header: t('common:fields.image'),
      cell: (p) => {
        const url = resolveImageUrl(p.images?.[0])
        return (
          <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-md border bg-muted">
            {url ? (
              <img src={url} alt={p.name} className="h-full w-full object-cover" />
            ) : (
              <ImageIcon className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        )
      },
    },
    {
      key: 'name',
      header: t('productsPage.colName'),
      cell: (p) => (
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5">
            <p className="truncate font-medium">{p.name}</p>
            {isSyncedProduct(p) && (
              <Badge
                variant="default"
                className="shrink-0 gap-1 px-1.5 py-0 text-[10px]"
                title={t('productsPage.syncedTitle', { src: PRODUCT_SOURCE_LABELS[p.externalSource!] ?? p.externalSource })}
              >
                <Lock className="h-2.5 w-2.5" /> {PRODUCT_SOURCE_LABELS[p.externalSource!] ?? p.externalSource}
              </Badge>
            )}
          </div>
          {p.keywords && <p className="truncate text-xs text-muted-foreground">{p.keywords}</p>}
          {p.stockAvailable != null && (
            <p className={cn('text-xs', p.stockAvailable > 0 ? 'text-muted-foreground' : 'text-destructive')}>
              {p.stockAvailable > 0 ? t('productsPage.inStock', { count: p.stockAvailable }) : t('productsPage.outOfStock')}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'code',
      header: t('productsPage.sku'),
      cell: (p) => <span className="tabular-nums text-muted-foreground">{p.code || '—'}</span>,
    },
    {
      key: 'price',
      header: t('common:fields.price'),
      align: 'right',
      cell: (p) => <span className="whitespace-nowrap font-medium">{formatProductPrice(p)}</span>,
    },
    {
      key: 'category',
      header: t('common:fields.category'),
      cell: (p) => <span className="text-muted-foreground">{p.category?.name || '—'}</span>,
    },
    {
      key: 'status',
      header: t('common:fields.status'),
      cell: (p) => (
        <Badge variant={productStatusVariant(p.status)}>
          {PRODUCT_STATUS_LABELS[p.status] ?? p.status}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (p) => (
        isSyncedProduct(p) ? (
          <div className="flex justify-end">
            <Button variant="ghost" size="icon" onClick={() => setViewing(p)} title={t('productsPage.viewReadonly')}>
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        ) : quyen.update || quyen.delete ? (
          <div className="flex justify-end gap-1">
            {quyen.update && (
              <Button variant="ghost" size="icon" onClick={() => setEditing(p)} title={t('common:actions.edit')}>
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            {quyen.delete && (
              <Button variant="ghost" size="icon" onClick={() => onDelete(p)} title={t('productsPage.delete')}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            )}
          </div>
        ) : null
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('productsPage.searchPlaceholder')}
            className="pl-9"
          />
        </div>
        <Select
          value={categoryId}
          onValueChange={(v) => {
            setCategoryId(v)
            setPage(1)
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder={t('common:fields.category')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('productsPage.allCategories')}</SelectItem>
            {(categories ?? []).map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={status}
          onValueChange={(v) => {
            setStatus(v)
            setPage(1)
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t('common:fields.status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('productsPage.allStatuses')}</SelectItem>
            {PRODUCT_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {PRODUCT_STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={source}
          onValueChange={(v) => {
            setSource(v)
            setPage(1)
          }}
        >
          <SelectTrigger className="w-[170px]">
            <SelectValue placeholder={t('common:fields.source')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('productsPage.allSources')}</SelectItem>
            <SelectItem value="internal">{t('productsPage.srcInternal')}</SelectItem>
            <SelectItem value="evotech">{t('productsPage.srcEvotech')}</SelectItem>
          </SelectContent>
        </Select>
        {quyen.create && (
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> {t('productsPage.add')}
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        loading={isLoading}
        rowKey={(p) => p.id}
        emptyTitle={isError ? t('productsPage.loadError') : t('productsPage.empty')}
      />

      {!!data && data.meta.total > 0 && (
        <Pagination page={page} limit={LIMIT} total={data.meta.total} onPageChange={setPage} />
      )}

      {viewing && <SyncedProductDialog product={viewing} onClose={() => setViewing(null)} />}

      {(creating || editing) && (
        <ProductDialog
          product={editing}
          categories={categories ?? []}
          open={creating || !!editing}
          onOpenChange={(v) => {
            if (!v) {
              setCreating(false)
              setEditing(null)
            }
          }}
        />
      )}
    </div>
  )
}

/** Xem sản phẩm đồng bộ (chỉ đọc) — sửa trên hệ thống gốc, eCDP tự cập nhật. */
function SyncedProductDialog({ product: p, onClose }: { product: Product; onClose: () => void }) {
  const { t } = useTranslation('aiComp')
  const src = PRODUCT_SOURCE_LABELS[p.externalSource ?? ''] ?? p.externalSource
  const specs = Object.entries(p.specs ?? {}).filter(([, v]) => v != null && v !== '')
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {p.name}
          </DialogTitle>
        </DialogHeader>
        <div className="flex gap-2 rounded-md border border-primary/20 bg-primary/5 p-3 text-xs text-muted-foreground">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
          <p>
            <Trans t={t} i18nKey="productsPage.syncedNote" values={{ src }} components={{ b: <b className="text-foreground" /> }} />
          </p>
        </div>
        <div className="space-y-3 text-sm">
          {p.images?.length > 0 && (
            <div className="flex gap-2 overflow-x-auto">
              {p.images.slice(0, 5).map((u) => (
                <img key={u} src={resolveImageUrl(u)} alt="" className="h-20 w-20 shrink-0 rounded-md border object-cover" />
              ))}
            </div>
          )}
          <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5">
            <dt className="text-muted-foreground">{t('productsPage.sku')}</dt>
            <dd>{p.code || '—'}</dd>
            <dt className="text-muted-foreground">{t('common:fields.price')}</dt>
            <dd className="font-medium">{formatProductPrice(p)}</dd>
            <dt className="text-muted-foreground">{t('productsPage.stockAvailable')}</dt>
            <dd>{p.stockAvailable ?? '—'}</dd>
            <dt className="text-muted-foreground">{t('common:fields.status')}</dt>
            <dd>
              <Badge variant={productStatusVariant(p.status)}>{PRODUCT_STATUS_LABELS[p.status] ?? p.status}</Badge>
            </dd>
            <dt className="text-muted-foreground">{t('productsPage.codeOf', { src })}</dt>
            <dd className="tabular-nums">
              itemNo {p.externalId}
              {p.externalParentId ? t('productsPage.group', { id: p.externalParentId }) : ''}
            </dd>
            {specs.map(([k, v]) => (
              <FragmentRow key={k} label={k} value={String(v)} />
            ))}
          </dl>
          {p.description && (
            <p className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
              {p.description}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common:actions.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function FragmentRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{value}</dd>
    </>
  )
}

function ProductDialog({
  product,
  categories,
  open,
  onOpenChange,
}: {
  product: Product | null
  categories: ProductCategory[]
  open: boolean
  onOpenChange: (v: boolean) => void
}) {
  const { t } = useTranslation('aiComp')
  const isEdit = !!product
  const create = useCreateProduct()
  const update = useUpdateProduct()
  const upload = useUploadProductImage()
  const fileRef = useRef<HTMLInputElement>(null)

  const [form, setForm] = useState({
    name: product?.name ?? '',
    code: product?.code ?? '',
    categoryId: product?.categoryId ?? NONE,
    priceType: product?.priceType ?? 'fixed',
    price: product?.price != null ? String(product.price) : '',
    priceMax: product?.priceMax != null ? String(product.priceMax) : '',
    currency: product?.currency ?? 'VND',
    status: product?.status ?? 'active',
    keywords: product?.keywords ?? '',
    description: product?.description ?? '',
  })
  const [images, setImages] = useState<string[]>(product?.images ?? [])

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const onPickImage = async (file?: File | null) => {
    if (!file) return
    upload.mutate(file, {
      onSuccess: (url) => {
        setImages([url])
        toast.success(t('productsPage.imageUploaded'))
      },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  const onSubmit = () => {
    if (!form.name.trim()) {
      toast.error(t('productsPage.nameRequired'))
      return
    }
    const hasPrice = form.priceType === 'fixed' || form.priceType === 'range'
    const payload: ProductInput = {
      name: form.name.trim(),
      code: form.code.trim() || null,
      categoryId: form.categoryId === NONE ? null : form.categoryId,
      priceType: form.priceType,
      price: hasPrice && form.price !== '' ? Number(form.price) : null,
      priceMax: form.priceType === 'range' && form.priceMax !== '' ? Number(form.priceMax) : null,
      currency: form.currency.trim() || 'VND',
      status: form.status,
      keywords: form.keywords.trim() || null,
      description: form.description.trim() || null,
      images,
    }

    const onSuccess = () => {
      toast.success(isEdit ? t('productsPage.updated') : t('productsPage.added'))
      onOpenChange(false)
    }
    const onError = (e: unknown) => toast.error(apiError(e))

    if (isEdit && product) {
      update.mutate({ id: product.id, data: payload }, { onSuccess, onError })
    } else {
      create.mutate(payload, { onSuccess, onError })
    }
  }

  const saving = create.isPending || update.isPending
  const previewUrl = resolveImageUrl(images[0])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('productsPage.editTitle') : t('productsPage.add')}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          {/* Ảnh */}
          <div className="flex items-center gap-4">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-lg border bg-muted">
              {previewUrl ? (
                <img src={previewUrl} alt="preview" className="h-full w-full object-cover" />
              ) : (
                <ImageIcon className="h-6 w-6 text-muted-foreground" />
              )}
            </div>
            <div className="space-y-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onPickImage(e.target.files?.[0])}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={upload.isPending}
              >
                <Upload className="h-4 w-4" />
                {upload.isPending ? t('common:status.loading') : t('productsPage.uploadImage')}
              </Button>
              {previewUrl && (
                <Button type="button" variant="ghost" onClick={() => setImages([])}>
                  <X className="h-4 w-4" /> {t('productsPage.removeImage')}
                </Button>
              )}
            </div>
          </div>

          <Field label={t('productsPage.nameLabel')}>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('productsPage.sku')}>
              <Input value={form.code} onChange={(e) => set('code', e.target.value)} />
            </Field>
            <Field label={t('common:fields.category')}>
              <Select value={form.categoryId} onValueChange={(v) => set('categoryId', v)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('productsPage.pickCategory')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('productsPage.uncategorized')}</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('productsPage.priceType')}>
              <Select value={form.priceType} onValueChange={(v) => set('priceType', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_TYPES.map((pt) => (
                    <SelectItem key={pt} value={pt}>
                      {PRICE_TYPE_LABELS[pt]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={t('common:fields.status')}>
              <Select value={form.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRODUCT_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {PRODUCT_STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          {(form.priceType === 'fixed' || form.priceType === 'range') && (
            <div className="grid grid-cols-3 gap-3">
              <Field label={form.priceType === 'range' ? t('productsPage.priceFrom') : t('common:fields.price')}>
                <Input
                  type="number"
                  value={form.price}
                  onChange={(e) => set('price', e.target.value)}
                />
              </Field>
              {form.priceType === 'range' && (
                <Field label={t('productsPage.priceTo')}>
                  <Input
                    type="number"
                    value={form.priceMax}
                    onChange={(e) => set('priceMax', e.target.value)}
                  />
                </Field>
              )}
              <Field label={t('productsPage.currency')}>
                <Input value={form.currency} onChange={(e) => set('currency', e.target.value)} />
              </Field>
            </div>
          )}

          <Field label={t('productsPage.keywords')}>
            <Input value={form.keywords} onChange={(e) => set('keywords', e.target.value)} />
          </Field>

          <Field label={t('common:fields.description')}>
            <Textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder={t('productsPage.descPlaceholder')}
            />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('productsPage.cancel')}
          </Button>
          <Button onClick={onSubmit} disabled={saving}>
            {saving ? t('common:status.saving') : t('common:actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ════════════════════════════════════════════════════════════════════
// TAB 2 — Danh mục sản phẩm (cây)
// ════════════════════════════════════════════════════════════════════
function ProductCategoriesTab() {
  const { t } = useTranslation('aiComp')
  const { data, isLoading, isError } = useProductCategories()
  const del = useDeleteProductCategory()
  const [editing, setEditing] = useState<ProductCategory | null>(null)
  const [creating, setCreating] = useState(false)
  const quyen = useProductPerms()

  const rows = useMemo(() => flattenTree(data ?? []), [data])

  const onDelete = (c: ProductCategory) => {
    if (!confirm(t('productsPage.confirmDeleteCat', { name: c.name })))
      return
    del.mutate(c.id, {
      onSuccess: () => toast.success(t('productsPage.catDeleted')),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  const columns: Column<ProductCategory & { depth: number }>[] = [
    {
      key: 'name',
      header: t('productsPage.catName'),
      cell: (c) => (
        <div className="flex items-center gap-2" style={{ paddingLeft: c.depth * 20 }}>
          <FolderTree className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="font-medium">{c.name}</span>
        </div>
      ),
    },
    {
      key: 'description',
      header: t('common:fields.description'),
      cell: (c) => <span className="text-muted-foreground">{c.description || '—'}</span>,
    },
    {
      key: 'count',
      header: t('productsPage.productCount'),
      align: 'right',
      cell: (c) => <span className="tabular-nums">{c.productCount}</span>,
    },
    ...(quyen.update || quyen.delete
      ? [
          {
            key: 'actions',
            header: '',
            align: 'right',
            cell: (c) => (
              <div className="flex justify-end gap-1">
                {quyen.update && (
                  <Button variant="ghost" size="icon" onClick={() => setEditing(c)} title={t('common:actions.edit')}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
                {quyen.delete && (
                  <Button variant="ghost" size="icon" onClick={() => onDelete(c)} title={t('productsPage.delete')}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            ),
          } satisfies Column<ProductCategory & { depth: number }>,
        ]
      : []),
  ]

  return (
    <div className="space-y-4">
      {quyen.create && (
        <div className="flex justify-end">
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> {t('productsPage.addCat')}
          </Button>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        loading={isLoading}
        rowKey={(c) => c.id}
        emptyTitle={isError ? t('productsPage.catLoadError') : t('productsPage.catEmpty')}
      />

      {(creating || editing) && (
        <ProductCategoryDialog
          category={editing}
          categories={data ?? []}
          open={creating || !!editing}
          onOpenChange={(v) => {
            if (!v) {
              setCreating(false)
              setEditing(null)
            }
          }}
        />
      )}
    </div>
  )
}

function ProductCategoryDialog({
  category,
  categories,
  open,
  onOpenChange,
}: {
  category: ProductCategory | null
  categories: ProductCategory[]
  open: boolean
  onOpenChange: (v: boolean) => void
}) {
  const { t } = useTranslation('aiComp')
  const isEdit = !!category
  const create = useCreateProductCategory()
  const update = useUpdateProductCategory()

  const [form, setForm] = useState({
    name: category?.name ?? '',
    parentId: category?.parentId ?? NONE,
    icon: category?.icon ?? '',
    description: category?.description ?? '',
  })
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const parentOptions = categories.filter((c) => c.id !== category?.id)

  const onSubmit = () => {
    if (!form.name.trim()) {
      toast.error(t('productsPage.catNameRequired'))
      return
    }
    const payload: ProductCategoryInput = {
      name: form.name.trim(),
      parentId: form.parentId === NONE ? null : form.parentId,
      icon: form.icon.trim() || null,
      description: form.description.trim() || null,
    }
    const onSuccess = () => {
      toast.success(isEdit ? t('productsPage.catUpdated') : t('productsPage.catAdded'))
      onOpenChange(false)
    }
    const onError = (e: unknown) => toast.error(apiError(e))
    if (isEdit && category) update.mutate({ id: category.id, data: payload }, { onSuccess, onError })
    else create.mutate(payload, { onSuccess, onError })
  }

  const saving = create.isPending || update.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? t('productsPage.catEditTitle') : t('productsPage.catAddTitle')}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <Field label={t('productsPage.catNameLabel')}>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} />
          </Field>
          <Field label={t('productsPage.parent')}>
            <Select value={form.parentId} onValueChange={(v) => set('parentId', v)}>
              <SelectTrigger>
                <SelectValue placeholder={t('productsPage.root')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{t('productsPage.root')}</SelectItem>
                {parentOptions.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t('productsPage.icon')}>
            <Input value={form.icon} onChange={(e) => set('icon', e.target.value)} />
          </Field>
          <Field label={t('common:fields.description')}>
            <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('productsPage.cancel')}
          </Button>
          <Button onClick={onSubmit} disabled={saving}>
            {saving ? t('common:status.saving') : t('common:actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
